import { useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { MapLoading } from "../App.styles";
import { useNpcsDataQuery } from "../npc/Npc.state";
import CustomMarkerOverlay from "../common/CustomMarkerOverlay";
import Circle from "../common/google-maps/Circle";
import npcIcon from "../images/robot/robot_ss.png"
import theme from "../themes";

const NPCMarkers = ({ onMarkersLoaded }) => {
    const { t } = useTranslation();
    const { game_id } = useParams();
    const { data: sourceData, isFetching } = useNpcsDataQuery(game_id);
    const { npcs = [] } = sourceData || {}

    // Get markers from tasks/challenges
    const markers = useMemo(() => {
        return npcs.filter(t => t.latitude && t.longitude).map(({ id, latitude, longitude, locks }) => ({
        id,
        lat: latitude,
        lng: longitude,
        locked: locks?.location?.radius > 0,
        radius: locks?.location?.radius,
        }))
    }, [npcs])

    // Filter to unique circles to prevent stacking multiple translucent 
    // circles at the same location, which causes them to render darker.
    const uniqueCircles = useMemo(() => {
        const seen = new Set();
        return markers.filter(m => {
            if (!m.lat || !m.lng || !m.locked) return false;
            const key = `${m.lat}-${m.lng}-${m.radius}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [markers]);

    useEffect(() => {
        if (onMarkersLoaded && markers) {
            onMarkersLoaded(markers);
        }
    }, [markers, onMarkersLoaded]);

    return (
        <>
            {(isFetching) && (
                <MapLoading>
                    {t(`Loading NPCs...`)}
                </MapLoading>
            )}
            {markers.map((location) => {
                const { id, locked } = location;
                return (
                    <CustomMarkerOverlay
                        key={`${id}`} 
                        position={location} 
                        id={id}
                        locked={locked}
                        isDynamicIcon={false}
                        icon={{
                            url: npcIcon,
                            scaledSize: window.google && window.google.maps ? new window.google.maps.Size(26, 32) : { width: 26, height: 32 }
                        }}
                        fillColor={theme.accent1Color}
                    />
                )
            })}
            {uniqueCircles.map((circle) => {
                const { lat, lng, radius } = circle;
                return (
                    <Circle
                        key={`${lat}-${lng}-${radius}`}
                        center={{ lat, lng }}
                        radius={radius} // Google Maps uses meters for radius
                        options={{
                            strokeColor: theme.accent1Color,
                            strokeOpacity: 0.5,
                            strokeWeight: 2,
                            fillColor: theme.accent1Color,
                            fillOpacity: 0.125,
                        }}
                    />
                )
            })}
        </>
    )
}

export default NPCMarkers;